const requiredRule = (itemValue = 'value', validationText = 'This field is required') => {
  return (v) => {
    if (v instanceof Array) {
      return !v || !!v.length || validationText;
    } else if (typeof v === 'object') {
      return !!v?.[itemValue] || (v && !!Object.values(v).length) || validationText;
    }

    return !!v || v === 0 || validationText;
  };
};

const minNumberRule = (number, validationText = `Min allowed value is ${number}`) => {
  return (v) => v === null || v === undefined || Number(v) >= number || validationText;
};

const ageValidationRule = (dayGetter, monthGetter, yearGetter, validationText = 'Age is less than 18 years') => {
  return () => {
    const day = dayGetter() || 1; // Use day 1 if day is null
    const month = monthGetter();
    const year = yearGetter();

    if (!month || !year) return validationText; // Ensure month and year are provided

    const currentDate = new Date();
    const birthDate = new Date(year, month - 1, day); // month is 0-indexed in JS Date

    // Calculate age
    let age = currentDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = currentDate.getMonth() - birthDate.getMonth();
    const dayDiff = currentDate.getDate() - birthDate.getDate();

    // Adjust age if the current date is before the birthday this year
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }

    return age >= 18 || validationText;
  };
};

const maxNumberRule = (number, validationText = `Max allowed value is ${number}`) => {
  return (v) => !v || Number(v) <= number || validationText;
};

const minLengthRule = (number, isObject = false, validationText = `Min length is ${number}`) => {
  if (!isObject) {
    return (v) => !v || (v && Object.values(v).length >= number) || validationText;
  }

  return (v) => !v || v.length >= number || validationText;
};

const maxLengthRule = (number, isObject = false, validationText = `Max length is ${number}`) => {
  if (!isObject) {
    return (v) => !v || (v && Object.values(v).length <= number) || validationText;
  }

  return (v) => !v || v.length <= number || validationText;
};

const fileExtensionRule = (
  allowedExtensions = ['png', 'jpg', 'jpeg'],
  validationText = `Only ${allowedExtensions.join(', ')} files are allowed`,
) => {
  return (file) => {
    if (!file) return true; // Handle case where no file is provided

    const extension = file.name.split('.').pop().toLowerCase();
    return allowedExtensions.includes(extension) || validationText;
  };
};

const fileSizeLimitRule = () => {
  return (value) => {
    if (!value) return true;
    const size = value.size / 1024 / 1024; // size in MB
    return size <= 10 || 'File size should be less than 10 MB';
  };
};

export const emailRule = () => {
  const emailPattern = /^[a-zA-Z0-9]+([._%+-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/;

  return (email) => {
    const isValidPattern = !!email && emailPattern.test(email);
    const containsUppercaseLetters = /[A-Z]/.test(email);

    if (!isValidPattern) return 'Not a valid email';
    if (containsUppercaseLetters) return 'The email address cannot contain capital letters';
    return true;
  };
};

export const usernameRule = () => {
  const validation = /^[0-9a-zA-Z]*$/;
  return (v) => (!!v && validation.test(v)) || 'Not a valid username';
};

export const nameRule = () => {
  const validation = /^[0-9a-zA-Z]*$/;

  return (v) => (!!v && validation.test(v)) || 'Not a valid name';
};

export const cryptoAddressRule = (cryptoName) => {
  const bitcoinValidation = /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/;
  const litecoinValidation = /^[LM3][a-km-zA-HJ-NP-Z1-9]{26,33}$/;
  const ethereumValidation = /^0x[a-fA-F0-9]{40}$/;
  const tetherValidation = /^0x[a-fA-F0-9]{40}$/;

  switch (cryptoName) {
    case 'Bitcoin Address':
      return (v) => {
        if (!v) {
          return 'Bitcoin address is required';
        }
        return bitcoinValidation.test(v) || 'Not a valid Bitcoin address';
      };
    case 'Litecoin Address':
      return (v) => {
        if (!v) {
          return 'Litecoin address is required';
        }
        return litecoinValidation.test(v) || 'Not a valid Litecoin address';
      };
    case 'Ethereum Address':
      return (v) => {
        if (!v) {
          return 'Ethereum address is required';
        }
        return ethereumValidation.test(v) || 'Not a valid Ethereum address';
      };
    case 'Tether Address':
      return (v) => {
        if (!v) {
          return 'Tether address is required';
        }
        return tetherValidation.test(v) || 'Not a valid Tether address';
      };
    default:
      return () => 'Invalid cryptocurrency name';
  }
};

export const onlyCharsRule = () => {
  const validation = /^[a-zA-Z]+$/;

  return (v) => (!!v && validation.test(v)) || 'Only english characters are allowed';
};

export const onlyNumbersRule = () => {
  const validation = /^[0-9]*$/;

  return (v) => (!!v && validation.test(v)) || 'Only numbers are allowed';
};

export const passwordValidation = () => {
  return (v) =>
    /^(?=.*[a-zA-Z])(?=.*\d).+/.test(v) ||
    'Password must be between 6 and 32 characters and contain at least one letter and one number';
};

export const fullNameValidation = () => {
  return (value) => {
    const words = value.trim().split(' ');

    if (words.length < 2 || words.includes('')) {
      return 'Please fill in your full name.';
    }

    if (!/^[\p{L}\p{Zs}]*$/gu.test(value)) {
      return 'Format not valid. Please use just letters.';
    }

    return true;
  };
};

export const noOnlySpaces = () => {
  return (v) => !!v?.trim?.() || 'This field is required';
};

export const nameWithSpecialCharsRule = () => {
  const allowedChars = /^[\p{L} .'-]+$/u;

  return (v) => !v || allowedChars.test(v) || 'No special characters allowed';
};

export const nameWithSpecialCharsAndNumbersRule = () => {
  const allowedChars = /^[\p{L}\p{N} .,'-]+$/u;

  return (v) => !v || allowedChars.test(v) || 'No special characters allowed';
};

export const onlyIntegersRule = () => {
  const validation = /^\d+$/;

  return (v) => (!!v && validation.test(v)) || 'Only integer numbers.';
};

const noLeadingSpaceRule = (validationText = 'No leading empty space') => {
  return (v) => !v.startsWith(' ') || validationText;
};

const rules = {
  required: requiredRule,
  min: minNumberRule,
  max: maxNumberRule,
  minLength: minLengthRule,
  maxLength: maxLengthRule,
  email: emailRule,
  username: usernameRule,
  name: nameRule,
  onlyChars: onlyCharsRule,
  onlyNumbers: onlyNumbersRule,
  crypto: cryptoAddressRule,
  password: passwordValidation,
  fullName: fullNameValidation,
  ageValidation: ageValidationRule,
  onlyInteger: onlyIntegersRule,
  onlyLetters: nameWithSpecialCharsRule,
  onlyLettersAndNumbers: nameWithSpecialCharsAndNumbersRule,
  fileExtension: fileExtensionRule,
  fileSizeLimit: fileSizeLimitRule,
  noLeadingSpace: noLeadingSpaceRule,
  noOnlySpaces: noOnlySpaces,
};

export default rules;
